import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, pluck, switchMap } from 'rxjs/operators';
import { User } from '../objects/user';
import { SavedInterlayer } from '../types';
import { APP_CONFIG } from './config.service';
import { UserApiService } from './user-api.service';
import { UserService } from './user.service';

export type InterlayerWritePayload = Pick<SavedInterlayer, 'name' | 'thickness' | 'weight'> &
  Pick<Partial<SavedInterlayer>, 'color'>;

/**
 * Interlayers under My account → My loads. The list is part of GET /api/user → loads.interlayers.
 * Mutations call save/update/delete then refresh user so loads stay in sync.
 */
@Injectable({
  providedIn: 'root',
})
export class InterlayersApiService {
  private url = 'http://localhost:8080/api/';

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private userApi: UserApiService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
  ) {
    APP_CONFIG()
      .pipe(pluck('baseUri'))
      .subscribe((base: string) => (this.url = base));
  }

  public getSavedInterlayers(): Observable<SavedInterlayer[]> {
    return this.userService.user.pipe(
      map((u) =>
        (u.loads?.interlayers ?? [])
          .map((item) => this.normalizeInterlayer(item))
          .filter((i) => this.isValidInterlayer(i)),
      ),
    );
  }

  public createSavedInterlayer(payload: InterlayerWritePayload): Observable<SavedInterlayer> {
    const body = { data: this.toRequestBody(payload) };
    return this.http.post<unknown>(this.url + 'user/load/interlayer/save', body).pipe(
      switchMap(() => this.userApi.getUser()),
      map((user: User) => this.resolveInterlayerAfterWrite(user, payload)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public updateSavedInterlayer(
    id: number,
    payload: InterlayerWritePayload,
  ): Observable<SavedInterlayer | undefined> {
    const body = { data: { id, ...this.toRequestBody(payload) } };
    return this.http.post<unknown>(this.url + 'user/load/interlayer/update', body).pipe(
      switchMap(() => this.userApi.getUser()),
      map((user: User) => this.resolveInterlayerById(user, id) ?? this.resolveInterlayerAfterWrite(user, payload)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public deleteSavedInterlayer(id: number): Observable<void> {
    return this.http
      .post<void>(this.url + 'user/load/interlayer/delete', { data: { interlayerId: id } })
      .pipe(
        switchMap(() => this.userApi.getUser().pipe(map((): void => undefined))),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  private toRequestBody(payload: InterlayerWritePayload): Record<string, unknown> {
    const body: Record<string, unknown> = {
      name: payload.name,
      thickness: payload.thickness,
      weight: payload.weight,
    };
    if (payload.color !== undefined && payload.color !== null) {
      body.color = payload.color;
    }
    return body;
  }

  private interlayersFromUser(user: User): SavedInterlayer[] {
    return (user.loads?.interlayers ?? [])
      .map((raw) => this.normalizeInterlayer(raw))
      .filter((i) => this.isValidInterlayer(i));
  }

  private resolveInterlayerById(user: User, id: number): SavedInterlayer | undefined {
    return this.interlayersFromUser(user).find((i) => i.id === id);
  }

  /** After create, pick the saved row from refreshed user (POST body may be empty). */
  private resolveInterlayerAfterWrite(user: User, payload: InterlayerWritePayload): SavedInterlayer {
    const list = this.interlayersFromUser(user);
    const matches = list.filter(
      (i) =>
        i.name === payload.name &&
        Math.abs(i.thickness - payload.thickness) < 1e-6 &&
        Math.abs((i.weight ?? 0) - payload.weight) < 1e-6,
    );
    const best = matches.sort((a, b) => b.id - a.id)[0];
    if (best) {
      return best;
    }
    const newest = [...list].sort((a, b) => b.id - a.id)[0];
    if (newest) {
      return newest;
    }
    return {
      id: -1,
      name: payload.name,
      thickness: payload.thickness,
      weight: payload.weight,
      ...(payload.color != null ? { color: payload.color } : {}),
    };
  }

  private isValidInterlayer(i: SavedInterlayer): boolean {
    return Boolean(i?.name?.trim()) && Number.isFinite(i.id) && i.id > 0;
  }

  private normalizeInterlayer(raw: unknown): SavedInterlayer {
    const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const id = Number(o.id ?? o.interlayerId);
    const weightRaw = o.weight;
    const weight =
      weightRaw !== undefined && weightRaw !== null && !Number.isNaN(Number(weightRaw))
        ? Number(weightRaw)
        : 0;
    return {
      id: Number.isFinite(id) ? id : 0,
      name: typeof o.name === 'string' ? o.name : typeof o.name === 'number' ? String(o.name) : '',
      thickness: Number(o.thickness ?? 0),
      weight,
      ...(o.color !== undefined && o.color !== null ? { color: Number(o.color) } : {}),
    };
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.status === 401) {
      this.router.navigate([this.translate.currentLang, 'auth'], {
        relativeTo: this.route,
      });
    } else if (error.status === 403) {
      window.location.reload();
    }
    return throwError(error);
  }
}
